import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import CalendarContainer from './calendar_container';
import ManagedInput from 'components/form/managed_input';
import { ApexDatePickerContext } from './context';
import { calculateDate, formatDate } from 'bundles/apex/dates/helpers';

import './styles.scss';

const clearRelativeDate = {
  relativeDateId: null,
  static: true
};

export default function ManagedApexDateInput({
  alignRight,
  className,
  dateNameWhitelist,
  deal,
  disabled,
  fields,
  name,
  required,
  value,
  onChange = () => {},
  onValueChange = () => {},
  ...props
}) {
  const dateFields = useMemo(() => {
    const dateFields = {
      ...pickBy(fields, field => field.isDate),
      ...pickBy(deal, value => value && value.date && value.adHoc)
    };

    let whitelistedFields;
    if (dateNameWhitelist) {
      whitelistedFields = {};
      for (const name of dateNameWhitelist) {
        const field = fields[name];
        if (field) {
          whitelistedFields[name] = fields[name];
        }
      }
    }

    return whitelistedFields || dateFields;
  }, [deal, fields, dateNameWhitelist]);
  const dateValues = useMemo(() => pick(deal, Object.keys(dateFields)), [deal, dateFields]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState();

  React.useEffect(() => {
    const oldValue = pickerValue || value;
    if (oldValue) {
      const date = calculateDate(deal, fields, name, oldValue);
      if (date !== oldValue.date) {
        const value = { ...oldValue, date };
        updateValue(value);
      }
    }
  }, [deal]);

  function openPicker() {
    setPickerOpen(true);
    setPickerValue({ ...value });
  }

  function closePicker() {
    setPickerOpen(false);
    setPickerValue(null);
  }

  function handleChange(changes) {
    const value = { ...pickerValue, ...changes };
    value.date = calculateDate(deal, fields, name, value);
    updateValue(value);
  }

  function updateValue(value) {
    const event = { target: { name, value } };
    onChange({ [name]: value }, event);
    onValueChange(value, event);
    setPickerValue(value);
  }

  const renderCalendarContainer = useMemo(() => {
    const MomoizedCalendarContainer = props => (
      <CalendarContainer
        alignRight={alignRight}
        {...props}
      />
    );
    return MomoizedCalendarContainer;
  }, []);

  value = pickerValue || value;

  return (
    <ApexDatePickerContext.Provider
      value={{
        dateFields,
        dateValues,
        name,
        value,
        onCancel: closePicker,
        onChange: handleChange
      }}
    >
      <ManagedInput
        calendarContainer={renderCalendarContainer}
        className={classNames('form-control', className)}
        disabled={disabled}
        inputValue={value?.static ? null : formatDate(deal, dateFields, value)}
        name="date"
        open={pickerOpen}
        popperClassName="apex-date-input-popper"
        required={required}
        type="date"
        value={value?.date}
        onChange={changes => handleChange({ ...clearRelativeDate, ...changes })}
        onClickOutside={closePicker}
        onInputClick={openPicker}
        {...props}
      />
    </ApexDatePickerContext.Provider>
  );
}

ManagedApexDateInput.propTypes = {
  alignRight: PropTypes.bool,
  className: PropTypes.string,
  dateNameWhitelist: PropTypes.arrayOf(PropTypes.string),
  deal: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  fields: PropTypes.objectOf(PropTypes.shape({
    isDate: PropTypes.bool,
  })).isRequired,
  name: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.shape({
    date: PropTypes.string,
    modifier: PropTypes.number,
    operator: PropTypes.string,
    relativeDateId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    static: PropTypes.bool,
    units: PropTypes.string,
  }),
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
};
